import { Avatar } from "../avatar/Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "../dropdownMenu/DropdownMenu";
import { Icon, IconProps } from "../icon/Icon";
import { ThemeSwitcher } from "../themeSwitcher/ThemeSwitcher";
import {
  activeCheck,
  avatarAndName,
  avatarStyle,
  itemIcon,
  nameBlock,
  nameStyle,
  subMenuItemLabel,
  subtitleStyle,
  userMenuTrigger
} from "./UserMenu.css";

/** One entry inside a {@link UserMenuSubMenu}. */
export interface UserMenuSubMenuItem {
  /** Menu-item label. */
  label: string;

  /**
   * Marks this entry as the active one: it renders with a check icon and
   * `aria-current`, and selecting it is a no-op.
   */
  isActive?: boolean;

  /** Called when the entry is picked (not called for the active entry). */
  onSelect?: () => void;
}

/** A nested submenu rendered above the plain menu items. */
export interface UserMenuSubMenu {
  /** Submenu trigger label. */
  label: string;

  /**
   * Disables the trigger and every entry — e.g. while the action a pick
   * starts is still in flight.
   */
  disabled?: boolean;

  /** The submenu's entries. */
  items: UserMenuSubMenuItem[];
}

interface UserMenuProps {
  items: {
    /**
     * The label of the menu item
     */
    label: string;

    /**
     * Optional icon to display next to the label.
     */
    icon?: IconProps["name"];

    /**
     * Optional onClick handler for the menu item.
     */
    onClick?: () => void;
  }[];

  /**
   * The name of the user to display in the menu.
   */
  userName: string;

  /**
   * URL of the user's profile image.
   */
  imageUrl?: string;

  /**
   * Small line rendered under the user name on the trigger — ambient context
   * the caller wants visible without opening the menu (e.g. the active
   * tenant).
   */
  subtitle?: string;

  /**
   * Nested submenus rendered above the plain items. The menu is purely
   * presentational here — what a submenu means (and whether to show one at
   * all) is the caller's policy.
   */
  subMenus?: UserMenuSubMenu[];
}

export function UserMenu({
  items,
  userName,
  imageUrl,
  subtitle,
  subMenus = []
}: UserMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={userMenuTrigger}>
        <div className={avatarAndName}>
          <div className={avatarStyle}>
            <Avatar userName={userName} src={imageUrl} />
          </div>
          <span className={nameBlock}>
            <span className={nameStyle}>{userName}</span>
            {subtitle ? (
              <span className={subtitleStyle}>{subtitle}</span>
            ) : null}
          </span>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent aria-describedby="dialog-content">
        {subMenus.map((subMenu) => (
          <DropdownMenuSub key={subMenu.label}>
            <DropdownMenuSubTrigger
              disabled={subMenu.disabled}
              aria-label={subMenu.label}
            >
              {subMenu.label}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent aria-label={subMenu.label}>
              {subMenu.items.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  disabled={subMenu.disabled}
                  aria-current={item.isActive ? "true" : undefined}
                  onSelect={() => {
                    if (!item.isActive) item.onSelect?.();
                  }}
                >
                  <span className={subMenuItemLabel}>{item.label}</span>
                  {item.isActive ? (
                    <span className={activeCheck}>
                      <Icon name="check" />
                    </span>
                  ) : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
        {items.map((item, index) => (
          <DropdownMenuItem
            key={index}
            onSelect={() => {
              item.onClick?.();
            }}
          >
            {item.icon ? (
              <span className={itemIcon}>
                <Icon name={item.icon} />
              </span>
            ) : undefined}
            {item.label}
          </DropdownMenuItem>
        ))}
        <ThemeSwitcher />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
